import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { searchDirectory } from "../../api/directoryApi";

/**
 * Pick a person, by name rather than by remembering their login.
 *
 * Two ways in, because the two habits are genuinely different. Somebody who knows who they want types two or
 * three letters and takes the type-ahead. Somebody who does NOT — the common case when an admin is routing a
 * workflow through a department they do not work in — presses Browse and gets a dialog opened on the people
 * this organization already routes work to, with org-wide search behind it.
 *
 * <b>Free text is never taken away.</b> The field's value is whatever is in the box, and a name the directory
 * does not know is kept exactly as typed, flagged with an advisory note rather than refused. Two real reasons:
 * a workflow may legitimately name an account that has not been created yet (routing designed ahead of a
 * hire), and the directory is a different SERVICE — if OATH is unreachable, a picker that refuses unknown
 * names would make the designer unusable rather than degraded. Nothing here validates; the workflow validator
 * on the server is what decides whether a definition may go live.
 *
 * The value written is the account's UserName, not its e-mail. They are frequently the same string and
 * occasionally not, and only UserName is what the task resolver matches on.
 *
 * Props:
 *   value        — current user name ("" when unset)
 *   onChange(v)  — called with the new user name, or null when cleared
 *   token        — bearer token for the directory service
 *   suggestions  — { recent: [{userName,count,lastUsedUtc}], frequent: [...] }, optional
 *   disabled     — read-only mode
 *   placeholder  — placeholder for the input
 *   id           — input id, for an external <label>
 */

// Resolved accounts live for the life of the page: the same handful of people are looked at repeatedly while
// somebody wires up a workflow, and re-asking the auth service on every keystroke and re-render is waste.
// Bounded so a long session browsing a large directory cannot grow it without limit.
const NAME_CACHE = new Map();
const NAME_CACHE_MAX = 500;

function remember(users) {
  for (const u of users || []) {
    if (!u || !u.userName) continue;
    const key = u.userName.toLocaleLowerCase();
    if (NAME_CACHE.has(key)) NAME_CACHE.delete(key);
    NAME_CACHE.set(key, u);
    if (NAME_CACHE.size > NAME_CACHE_MAX) {
      NAME_CACHE.delete(NAME_CACHE.keys().next().value);
    }
  }
}

function lookup(userName) {
  if (!userName) return null;
  return NAME_CACHE.get(userName.toLocaleLowerCase()) || null;
}

/** "Ayşe Yılmaz" when we know it, the bare user name when we do not. Never blank. */
function displayName(userName) {
  const hit = lookup(userName);
  return (hit && hit.fullName) || userName || "";
}

/** Initials for the avatar chip. Falls back to the first letter of the login for a single-word name. */
function initials(userName) {
  const name = displayName(userName).trim();
  if (!name) return "?";
  const parts = name.split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toLocaleUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toLocaleUpperCase();
}

/** One row in either the type-ahead or the dialog. */
function PersonRow({ user, subtitle, active, onPick, id }) {
  const name = user.fullName || user.userName;
  const second = subtitle || (user.fullName ? user.userName : user.email) || "";
  return (
    <li
      id={id}
      role="option"
      aria-selected={!!active}
      className={`upk-row${active ? " is-active" : ""}`}
      // onMouseDown, not onClick: the input's blur fires first on click and would close the list underneath
      // the pointer before the click landed.
      onMouseDown={(e) => {
        e.preventDefault();
        onPick(user);
      }}
    >
      <span className="upk-avatar" aria-hidden="true">{initials(user.userName)}</span>
      <span className="upk-row-text">
        <span className="upk-row-name">{name}</span>
        {second && second !== name && <span className="upk-row-sub">{second}</span>}
      </span>
    </li>
  );
}

export default function UserPicker({
  value = "",
  onChange,
  token,
  suggestions = null,
  disabled = false,
  placeholder,
  id,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [active, setActive] = useState(-1);
  const [browsing, setBrowsing] = useState(false);
  // Bumped on every resolution so the memo below recomputes off the shared cache, which React cannot observe.
  const [resolved, setResolved] = useState(0);

  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  // Guards against an older, slower request overwriting a newer one's results.
  const seq = useRef(0);

  const current = value || "";
  const known = useMemo(() => lookup(current), [current, resolved]); // eslint-disable-line react-hooks/exhaustive-deps

  const runSearch = useCallback(
    async (q, take = 8) => {
      if (!token) return { users: [], total: 0, stale: false };
      const mine = ++seq.current;
      setLoading(true);
      try {
        const res = await searchDirectory(token, q, take);
        const users = (res && res.users) || [];
        remember(users);
        if (mine === seq.current) {
          setUnavailable(false);
          setResolved((n) => n + 1);
        }
        return { users, total: (res && res.total) || users.length, stale: mine !== seq.current };
      } catch {
        // Soft failure on purpose — see the note at the top of the file. The field stays typable.
        if (mine === seq.current) setUnavailable(true);
        return { users: [], total: 0, stale: mine !== seq.current };
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    },
    [token]
  );

  // Resolve the display name of whatever is already in the field, once, so an existing workflow shows
  // "Ayşe Yılmaz" rather than a bare login the moment it is opened.
  useEffect(() => {
    if (!current || !token || lookup(current)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await searchDirectory(token, current, 5);
        if (cancelled) return;
        remember((res && res.users) || []);
        setResolved((n) => n + 1);
      } catch {
        /* advisory only */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [current, token]);

  // Debounced type-ahead. 250ms is the usual "stopped typing" threshold; below it every keystroke is a round
  // trip to a second service.
  useEffect(() => {
    if (!open || disabled) return undefined;
    const term = current.trim();
    const handle = window.setTimeout(async () => {
      const res = await runSearch(term, 8);
      if (!res.stale) setResults(res.users || []);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [current, open, disabled, runSearch]);

  // Close on an outside click. Escape is handled on the input so it does not swallow the page's own handlers.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const pick = useCallback(
    (user) => {
      remember([user]);
      setResolved((n) => n + 1);
      onChange(user.userName || null);
      setOpen(false);
      setActive(-1);
      setBrowsing(false);
    },
    [onChange]
  );

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setActive(-1);
      return;
    }
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setOpen(true);
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      pick(results[active]);
    }
  };

  const listId = `${id || "upk"}-list`;
  // "Typed a name nobody has" is only worth saying once the directory has actually answered — otherwise a
  // blank field or an in-flight lookup would accuse the person of a mistake they have not made.
  const showUnknown = !!current && !known && !loading && !unavailable && token;

  return (
    <div className="upk" ref={wrapRef}>
      <div className="upk-control">
        <input
          id={id}
          ref={inputRef}
          type="text"
          className="upk-input"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
          autoComplete="off"
          value={current}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => {
            onChange(e.target.value || null);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        <button
          type="button"
          className="upk-browse"
          disabled={disabled}
          onClick={() => {
            setOpen(false);
            setBrowsing(true);
          }}
          title={t("userPicker.browse", { defaultValue: "Browse people" })}
          aria-label={t("userPicker.browse", { defaultValue: "Browse people" })}
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="6" cy="5" r="2.6" stroke="currentColor" strokeWidth="1.3" />
            <path d="M1.7 13c0-2.4 1.9-3.9 4.3-3.9s4.3 1.5 4.3 3.9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            <path d="M11.4 3.2a2.2 2.2 0 0 1 0 4.1M12.6 9.5c1.2.5 2 1.7 2 3.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {known && known.fullName && (
        <div className="upk-note upk-note--ok">
          {known.fullName}
          {known.email && known.email !== known.fullName ? ` · ${known.email}` : ""}
        </div>
      )}
      {showUnknown && (
        <div className="upk-note upk-note--warn">
          {t("userPicker.notRegistered", {
            defaultValue: "No account matches this name — it will be kept exactly as typed.",
          })}
        </div>
      )}
      {unavailable && (
        <div className="upk-note upk-note--warn">
          {t("userPicker.unavailable", {
            defaultValue: "The people directory could not be reached — type a user name instead.",
          })}
        </div>
      )}

      {open && !disabled && (loading || results.length > 0) && (
        <ul className="upk-menu" id={listId} role="listbox">
          {loading && results.length === 0 && (
            <li className="upk-empty">{t("userPicker.searching", { defaultValue: "Searching…" })}</li>
          )}
          {results.map((u, i) => (
            <PersonRow
              key={u.userName || i}
              id={`${listId}-${i}`}
              user={u}
              active={i === active}
              onPick={pick}
            />
          ))}
        </ul>
      )}

      {browsing && (
        <UserPickerDialog
          token={token}
          suggestions={suggestions}
          onPick={pick}
          onClose={() => setBrowsing(false)}
        />
      )}
    </div>
  );
}

/**
 * The Browse dialog: recents and frequents when nothing is typed, org-wide search when something is.
 *
 * Recents/frequents come from workflow history rather than the directory — see the server's
 * Workflow/AssigneeSuggestions — so this opens on the dozen people this organization actually routes work to
 * instead of the alphabetical head of a staff list, which is almost never who is wanted.
 */
function UserPickerDialog({ token, suggestions, onPick, onClose }) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const fieldRef = useRef(null);
  const seq = useRef(0);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const focus = window.setTimeout(() => fieldRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(focus);
    };
  }, [onClose]);

  useEffect(() => {
    if (!token) return undefined;
    const mine = ++seq.current;
    setLoading(true);
    const handle = window.setTimeout(async () => {
      try {
        // A blank query returns the head of the directory rather than nothing, so the dialog is browsable
        // by someone who does not know any part of the name.
        const res = await searchDirectory(token, q.trim(), 50);
        if (mine !== seq.current) return;
        const users = (res && res.users) || [];
        remember(users);
        setRows(users);
        setTotal((res && res.total) || users.length);
        setFailed(false);
      } catch {
        if (mine === seq.current) {
          setRows([]);
          setTotal(0);
          setFailed(true);
        }
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, q.trim() ? 250 : 0);
    return () => window.clearTimeout(handle);
  }, [q, token]);

  const searching = q.trim().length > 0;
  const recent = (suggestions && suggestions.recent) || [];
  const frequent = (suggestions && suggestions.frequent) || [];
  // Frequent repeats recent almost entirely in a small organization; drop the overlap rather than show the
  // same three people twice under two headings.
  const recentKeys = new Set(recent.map((r) => (r.userName || "").toLocaleLowerCase()));
  const frequentOnly = frequent.filter((f) => !recentKeys.has((f.userName || "").toLocaleLowerCase()));

  const asUser = (s) => lookup(s.userName) || { userName: s.userName, fullName: "", email: "" };

  return (
    <div className="dbModalOverlay" onClick={onClose}>
      <div className="dbModalCard upk-dialog" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="drawer-close-btn drawer-close-btn-abs"
          onClick={onClose}
          aria-label={t("common.close", { defaultValue: "Close" })}
          title={t("common.close", { defaultValue: "Close" })}
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
            <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>

        <div className="dbModalTitle">{t("userPicker.title", { defaultValue: "Choose a person" })}</div>

        <input
          ref={fieldRef}
          type="text"
          className="dbModalConfirmInput upk-dialog-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("userPicker.searchPlaceholder", {
            defaultValue: "Search by name, user name or e-mail",
          })}
        />

        <div className="upk-dialog-body">
          {!searching && recent.length > 0 && (
            <>
              <div className="upk-group">{t("userPicker.recent", { defaultValue: "Recently assigned" })}</div>
              <ul className="upk-list" role="listbox">
                {recent.map((s) => (
                  <PersonRow key={`r-${s.userName}`} user={asUser(s)} onPick={onPick} />
                ))}
              </ul>
            </>
          )}

          {!searching && frequentOnly.length > 0 && (
            <>
              <div className="upk-group">{t("userPicker.frequent", { defaultValue: "Assigned most often" })}</div>
              <ul className="upk-list" role="listbox">
                {frequentOnly.map((s) => (
                  <PersonRow
                    key={`f-${s.userName}`}
                    user={asUser(s)}
                    subtitle={t("userPicker.usedCount", {
                      // `n`, not `count`: `count` would put i18next into plural-key resolution and need a
                      // _one/_other pair per language, for a hint that does not warrant 46 keys.
                      defaultValue: "used {{n}} times",
                      n: s.count || 0,
                    })}
                    onPick={onPick}
                  />
                ))}
              </ul>
            </>
          )}

          <div className="upk-group">
            {searching
              ? t("userPicker.matches", { defaultValue: "Matches" })
              : t("userPicker.directory", { defaultValue: "Everyone in your organization" })}
            {total > rows.length && (
              <span className="upk-group-count">
                {t("userPicker.showing", {
                  defaultValue: "showing {{shown}} of {{total}}",
                  shown: rows.length,
                  total,
                })}
              </span>
            )}
          </div>

          {loading && <div className="upk-empty">{t("userPicker.searching", { defaultValue: "Searching…" })}</div>}

          {!loading && failed && (
            <div className="upk-empty">
              {t("userPicker.unavailable", {
                defaultValue: "The people directory could not be reached — type a user name instead.",
              })}
            </div>
          )}

          {!loading && !failed && rows.length === 0 && (
            <div className="upk-empty">{t("userPicker.noMatches", { defaultValue: "Nobody matches that." })}</div>
          )}

          {!loading && rows.length > 0 && (
            <ul className="upk-list" role="listbox">
              {rows.map((u) => (
                <PersonRow key={u.userName} user={u} onPick={onPick} />
              ))}
            </ul>
          )}
        </div>

        <div className="dbModalConfirmActions">
          <button type="button" className="dbModalCancel" onClick={onClose}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
        </div>
      </div>
    </div>
  );
}
